/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { FunctionComponent, useState } from "react";

import { StrapiPricingPage } from "@/types/strapi";

import { BorderRadiuses } from "@/tokens/border";
import { Colors } from "@/tokens/color";
import {
    ColumnGaps,
    NavigationSpacerHeightWithHalfColumnGap,
    Spacing,
} from "@/tokens/spacing";

import { GridColumn } from "@/ui/atoms/grid_column";
import { GridContainer } from "@/ui/atoms/grid_container";
import { PricingMatrixPlanHeaderColumn } from "@/ui/atoms/pricing_matrix_plan_header_column";
import { Text } from "@/ui/atoms/text";
import { RichText } from "@/ui/molecules/rich_text";
import { PricingPlansMatrix } from "@/ui/wrappers/pricing_plans_matrix";

import { useShowFullPricingMatrix } from "@/util/hooks/pricing_hooks";
import { useTypedTheme } from "@/util/hooks/theme_hooks";
import { buildStylesByBreakpoint } from "@/util/style_util";
import { convertToRem } from "@/util/ui_util";

export type SmallViewportPricingCategory = "professional" | "enterprise";

export const PlanMatrix: FunctionComponent<StrapiPricingPage> = (props) => {
    /**
     * Effects
     */
    const showFullPricingMatrix = useShowFullPricingMatrix();

    /**
     * State Management
     */
    // Only two plans are shown at a time on small viewports, these are their categories
    const [smallViewportPlanCategory, setSmallViewportPlanCategory] =
        useState<SmallViewportPricingCategory>("professional");

    /**
     * Styles
     */
    const theme = useTypedTheme();

    const stickyNavContainer = css(
        {
            background: Colors[theme.backgrounds.backgroundPrimary],
            position: "sticky",
            zIndex: "10",
        },
        buildStylesByBreakpoint("marginBottom", {
            extraSmall: Spacing["spacing-6"],
            medium: Spacing["spacing-8"],
        }),
        buildStylesByBreakpoint("top", NavigationSpacerHeightWithHalfColumnGap),
    );

    const navGridContainerStyles = css({
        borderBottom: `1px solid ${Colors[theme.ui.uiBorder]}`,
    });

    const navHeaderTitleStyles = css({
        padding: `${Spacing["spacing-4"]} 0`,
        whiteSpace: "nowrap",
    });

    const planRowContainerStyles = css(
        {
            alignItems: "stretch",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-evenly",
        },
        buildStylesByBreakpoint("gap", {
            extraSmall: Spacing["spacing-3"],
            medium: Spacing["spacing-4"],
            large: Spacing["spacing-6"],
        }),
    );

    const planCategoryToggleContainerStyles = css(
        {
            alignItems: "stretch",
            background: Colors["lighten-5"],
            borderRadius: BorderRadiuses["borderLarge"],
            gap: Spacing["spacing-1"],
            gridTemplateColumns: "1fr 1fr",
            height: convertToRem(74),
            marginTop: Spacing["spacing-6"],
            padding: Spacing["spacing-2"],
        },
        buildStylesByBreakpoint("display", {
            extraSmall: "grid",
            medium: "none",
        }),
    );

    const planCategoryToggleStyles = css({
        alignItems: "center",
        border: "none",
        borderRadius: BorderRadiuses["borderSmall"],
        cursor: "pointer",
        display: "flex",
        justifyContent: "center",
        outline: "none",
        padding: Spacing["spacing-4"],
    });

    const professionalPlanCategoryToggleStyles = css(planCategoryToggleStyles, {
        background:
            smallViewportPlanCategory === "professional"
                ? Colors["lighten-5"]
                : "none",
    });

    const enterprisePlanCategoryToggleStyles = css(planCategoryToggleStyles, {
        background:
            smallViewportPlanCategory === "enterprise"
                ? Colors["lighten-5"]
                : "none",
    });

    const planMatrixFooterStyles = css(
        {
            textAlign: "center",
        },
        buildStylesByBreakpoint("marginTop", ColumnGaps),
    );

    /**
     * Rendering
     */
    const renderMobilePlanToggle = () => {
        if (showFullPricingMatrix) {
            return null;
        }

        return (
            <GridColumn
                className={planCategoryToggleContainerStyles}
                columnSpan={6}
            >
                <button
                    css={professionalPlanCategoryToggleStyles}
                    onClick={() => {
                        setSmallViewportPlanCategory("professional");
                    }}
                >
                    <Text
                        color={
                            smallViewportPlanCategory === "professional"
                                ? theme.text.textEmphasis
                                : theme.text.textPrimary
                        }
                        fontSize="TextSmall"
                        fontWeight="medium"
                        textAlign="center"
                    >
                        Professional Developers
                    </Text>
                </button>

                <button
                    css={enterprisePlanCategoryToggleStyles}
                    onClick={() => {
                        setSmallViewportPlanCategory("enterprise");
                    }}
                >
                    <Text
                        color={
                            smallViewportPlanCategory === "enterprise"
                                ? theme.text.textPrimary
                                : theme.text.textSecondary
                        }
                        fontSize="TextSmall"
                        fontWeight="medium"
                        textAlign="center"
                    >
                        Business and Enterprise
                    </Text>
                </button>
            </GridColumn>
        );
    };

    const renderPlanMatrixHeader = () => {
        const { Plans } = props;

        if (!Plans) {
            return null;
        }

        const _filteredPlans = Plans.filter((_plan) => {
            if (!showFullPricingMatrix) {
                if (smallViewportPlanCategory === "professional") {
                    return _plan.Name === "Free" || _plan.Name === "Pro";
                } else {
                    return _plan.Name === "Team" || _plan.Name === "Enterprise";
                }
            }
            return true;
        });

        return (
            <GridColumn
                className={planRowContainerStyles}
                columnSpan={{ extraSmall: 6, medium: 9 }}
                columnStart={{ extraSmall: 1, medium: 4 }}
            >
                {_filteredPlans.map((_plan) => {
                    return (
                        <PricingMatrixPlanHeaderColumn
                            {..._plan}
                            key={`pricing-matrix-plan-header::header-item-${_plan.id}`}
                        />
                    );
                })}
            </GridColumn>
        );
    };

    return (
        <section css={{ marginTop: Spacing["spacing-18"] }}>
            <div css={stickyNavContainer}>
                <GridContainer className={navGridContainerStyles}>
                    <Text
                        className={navHeaderTitleStyles}
                        columnSpan={{ extraSmall: 6, medium: 3 }}
                        columnStart={1}
                        fontSize="PricingMatrixNavHeadline"
                        tag="h2"
                        textAlign={{
                            extraSmall: "center",
                            medium: "left",
                        }}
                        themeKey="headlinePrimary"
                    >
                        Compare Features
                    </Text>

                    {renderMobilePlanToggle()}

                    {renderPlanMatrixHeader()}
                </GridContainer>
            </div>

            {props.Plan_Matrix &&
                props.Plan_Matrix.map((_matrix) => (
                    <PricingPlansMatrix
                        currentPlanCategory={smallViewportPlanCategory}
                        key={`pricing-plans::matrix-item-${_matrix.id}`}
                        Plan_Matrix_Rows={_matrix.Plan_Matrix_Rows}
                        shouldShowFullMatrix={showFullPricingMatrix}
                        Title={_matrix.Title}
                    />
                ))}

            <div css={planMatrixFooterStyles}>
                {props.Plans_Matrix_Footer && (
                    <RichText config="Longform">
                        {props.Plans_Matrix_Footer}
                    </RichText>
                )}
            </div>
        </section>
    );
};
